/**
 * Agent Store（F-M7 保守拆分方案）
 *
 * 🔵 F-M7 评估：当前 useAgentStore 被 34 个文件 import（>20 阈值），实际拆分为
 *   独立 store 风险过高，故采用「单 store + slice 分组注释」方案：
 *   - Slice 1 Session：会话管理（messages / sessionId / projectId / agents / 历史）
 *   - Slice 2 Stream ：流式状态 + 任务流（activeTask）+ 思考计时
 *   - Slice 3 Config ：模型选择 / searchMode / skills（用户偏好型配置）
 *
 * 未来若需进一步拆分：建议保持 useAgentStore 单一导出，内部按 slice 拆为
 *   createSessionSlice / createStreamSlice / createConfigSlice 三个工厂函数。
 */
import { create } from 'zustand';
import type { SkillInfo, ChatModel, AgentProfile, ProcessLogEntry } from '@/types/agent';
import type { Message, AttachmentItem, ChatHistoryEntry, IntentInfo } from '@/components/agent/types';
import type { ActiveTask, TaskStage, TaskAskData, TimelineItem } from '@/components/agent/task/types';
import { AgentService } from '@/services/agent/AgentService';
import { unifiedStorage } from '@/services/unifiedStorage';
import { initCrossTabSync, notifyOtherTabs, markLocalUpdate } from '@/services/crossTabSync';
import { modelApi } from '@/services/modelApi';
import { createSetter } from './utils/create-setter';

/** LLM 模型列表拉取失败时的兜底（与后端 models.json category=llm 对齐） */
export const FALLBACK_LLM_MODELS: ChatModel[] = [
  { id: 'grsai-gemini-2.5-flash', name: 'Gemini 2.5 Flash', backend: 'grsai', supportedModes: ['chat'] },
  { id: 'grsai-gemini-3-flash', name: 'Gemini 3 Flash', backend: 'grsai', supportedModes: ['chat'] },
  { id: 'grsai-gemini-3.1-pro', name: 'Gemini 3.1 Pro', backend: 'grsai', supportedModes: ['chat'] },
  { id: 'grsai-gemini-3.1-flash-lite', name: 'Gemini 3.1 Flash Lite', backend: 'grsai', supportedModes: ['chat'] },
  { id: 'grsai-gpt-4o', name: 'GPT-4o', backend: 'grsai', supportedModes: ['chat'] },
  { id: 'grsai-deepseek-r1', name: 'DeepSeek R1', backend: 'grsai', supportedModes: ['chat'] },
];

/** 对话模型默认值（与 unifiedStorage.mergeAgentStates 的兜底保持一致） */
export const DEFAULT_CHAT_MODEL_ID = 'grsai-gemini-3.1-flash-lite';

/**
 * 幽灵选中值清洗（2026-10-12）：currentModelId 经 unifiedStorage 持久化（本地+
 * 远端合并恢复），可能残留旧版本模型列表中已被下线的模型（如 gpt-4o-mini）。
 * 此类幽灵 id 在模型选择器中因 find 不到而显示首个可用模型，但发送请求仍携带，
 * 上游返回 400 "model not found"。模型列表就绪后校验回退，单点根治。
 */
function sanitizeCurrentModelId(currentId: string, models: ChatModel[]): string {
  return models.some((m) => m.id === currentId) ? currentId : DEFAULT_CHAT_MODEL_ID;
}

let _modelsFetchPromise: Promise<void> | null = null;

// ============================================================================
// 🔴 方案C: per-session 并行活容器 + 流式写入分流
// ----------------------------------------------------------------------------
// 背景：store 的流式字段（streamingText/activeTask/activeProcessLogs...）是全局
// 单一副本，仅属于"当前视图会话"。用户切走会话时旧 SSE 流必须 abort（否则
// chunk 写入会污染新会话视图），切回只能走 DB 恢复（网络往返 + 富格式重建
// 的可见滞后）。
//
// 方案C 三件套：
//   1. sessionLiveStates 容器（本文件）：每个流式中的会话持有独立活状态副本
//   2. SSE 保活（useChunkHandlers/useStreamLifecycle/useChatHistory）：切走不
//      abort，chunk handler 按闭包 streamSessionId 分流写入归属容器
//   3. 切回换指针（useChatHistory.loadSessionMessages）：容器命中则同步恢复
//      视图（零网络零滞后），再异步 DB resync 校正
//
// 分流判据：chunk handler 闭包的 streamSessionId（发送时刻捕获的流归属，权威）
// ≠ store.sessionId（视图会话）时为后台流，写入走带 sid 参数的容器版 API
// （updateLiveActiveTask / writeLiveStreamingText / bumpLiveChunkReceivedAt），
// Map mutate 非响应式、不触发渲染，视图不受后台流干扰；切回换指针后两者相等，
// 分流自动失效、写入回到视图。现有视图版 action 完全不动。
// （不用 activeStreamSessionId 单值字段做判据：A 会话后台流中、B 会话再发送
//  会覆盖单值，导致 A 的 chunk 误写视图；闭包 sid 天然支持任意多后台流。）
// ============================================================================

/** 🔴 FE-1: 后台流缓冲的结构化事件条目（guard 路由进容器，切回依序重放） */
export interface BufferedStreamEvent {
  /** 事件类型（onSearchResult / onTaskAsk / onTaskStage ...） */
  type: string;
  /** 事件类别：state=状态设置（同 type 仅保留最新）/ append=追加类（超限丢最旧） */
  kind: 'state' | 'append';
  /** 重放函数（闭包捕获原回调与参数；切回后归属一致，守卫自然放行） */
  replay: () => void;
}

/** 🔴 FE-1: 结构化事件缓冲上限（防爆内存；超限优先丢最旧追加类事件） */
const LIVE_EVENT_BUFFER_MAX = 500;

/** 🔴 FE-6: 最后活跃会话标记的 localStorage key（读侧 useChatHistory._readLastSessionMarker） */
const LAST_SESSION_KEY = 'bongai_last_session';

/**
 * 🔴 FE-6: 写最后活跃会话标记（{ sessionId, projectId } 复合）。
 * 供刷新后 tryRestore / checkProjectReady 自动恢复"最后生成中会话"；projectId
 * 一并写入，读侧按当前项目校验，防跨项目恢复错会话。sessionId 为 null（新聊天）
 * 时清除标记，防重进项目误开旧会话。存储不可用（隐私模式/配额满）静默降级。
 *
 * 🔴 FE-6-修复(2026-08-28): 仅 sessionId 显式为 null 才清除；projectId 未就绪时
 * 保留原标记不写。原实现 sessionId 非空但 projectId 为 null 也走清除分支——
 * 刷新恢复路径（checkProjectReady 补偿 setSessionId(marker.sessionId)）执行时
 * agentStore.projectId 常晚于 sessionId 就绪，恢复成功的同时把 marker 清掉，
 * 导致"首次刷新可恢复、再次刷新空对话"。
 */
function _writeLastSessionMarker(sessionId: string | null, projectId: string | null): void {
  try {
    if (sessionId === null) {
      localStorage.removeItem(LAST_SESSION_KEY);
      return;
    }
    if (!sessionId || !projectId) return; // projectId 未就绪：保留原标记
    localStorage.setItem(LAST_SESSION_KEY, JSON.stringify({ sessionId, projectId }));
  } catch { /* 静默降级 */ }
}

/** 🔴 方案C: 单会话的并行活状态（stage 于切走时，SSE 保活期间持续更新） */
export interface SessionLiveState {
  /** 最后更新时间（诊断/过期清理用） */
  savedAt: number;
  /** 该会话 SSE 流的 controller（切走时从视图移交，切回时归还） */
  abortController: AbortController | null;
  /** 切走瞬间的消息列表（切回换指针立即可渲染） */
  messages: Message[];
  streamingText: string | null;
  activeAssistantIntro: string | null;
  activeProcessLogs: ProcessLogEntry[];
  activeTask: ActiveTask | null;
  isLoading: boolean;
  isThinking: boolean;
  thinkingSeconds: number;
  /** 该会话自己的 SSE chunk 时间戳（后台轮询 SSE 活跃判断用，不与视图混用） */
  lastChunkReceivedAt: number | null;
  currentIntent: IntentInfo | null;
  /** 🔴 FE-1: 切走期间到达的结构化事件缓冲（guard 路由写入，切回重放） */
  pendingEvents: BufferedStreamEvent[];
}

interface AgentState {
  // ==========================================================================
  // 🟦 Slice 1: Session — 会话管理（sessionId / projectId / messages / 历史 / agent 列表）
  // ==========================================================================
  messages: Message[];
  sessionId: string | null;
  projectId: string | null;
  agents: AgentProfile[];
  currentAgentId: string | null;
  chatHistory: ChatHistoryEntry[];
  selectedHistoryId: string | null;
  showHistoryModal: boolean;
  // Session actions
  setSessionId: (sessionId: string | null) => void;
  setProjectId: (projectId: string | null) => void;
  // P0-F3/P1-F3: setMessages 扩展 extra 参数支持原子写入 messages + 其他字段（如 STREAMING_RESET），
  // 替代 5+ 处 useAgentStore.setState 绕过 action 的写法，确保跨标签页广播与 saveToLocal 始终触发。
  setMessages: (
    messages: Message[] | ((prev: Message[]) => Message[]),
    extra?: Partial<AgentState>,
  ) => void;
  setCurrentAgentId: (agentId: string | null) => void;
  loadAgents: () => Promise<void>;
  ensureSession: (projectId: string) => Promise<string>;
  resetSession: () => void;
  saveToLocal: () => Promise<void>;
  setChatHistory: (history: ChatHistoryEntry[] | ((prev: ChatHistoryEntry[]) => ChatHistoryEntry[])) => void;
  setSelectedHistoryId: (id: string | null) => void;
  setShowHistoryModal: (show: boolean | ((prev: boolean) => boolean)) => void;

  // ==========================================================================
  // 🟦 Slice 2: Stream — 流式状态 + 任务流（activeTask）+ 思考计时
  // ==========================================================================
  abortController: AbortController | null;
  isLoading: boolean;
  isThinking: boolean;
  thinkingSeconds: number;
  // 🔴 P1-18: 统一为结构化对象 IntentInfo | null（替代原 string）
  currentIntent: IntentInfo | null;
  input: string;
  // 🔴 P1-17 / M12: streamingText 与 activeTask.content 的关系与统一读点规则
  // - streamingText: 文本场景的流式显示源 + "是否正在流式"标志（非 null 表示流式中），是流式中的临时值
  // - activeTask.content: 搜索/确认场景的流式显示源（由 chunk handler 的 markdown 缓冲逻辑维护），是最终值
  // - 🔴 M12 统一读点规则：所有读取点必须使用 `activeTask?.content || streamingText` 回退顺序
  //   理由：activeTask.content 是最终值，streamingText 是流式中的临时值，优先读最终值更合理
  //   适用位置：useStreamingPhase / pushTimelineContent / finalizeTextMsg / StreamingContentRenderer 等
  // - 不完全合并的原因：两者更新路径不同（文本走 setStreamingText，搜索走 updateActiveTask），
  //   且 streamingText 还承担"是否流式中"的标志职责，合并会引入高渲染开销与语义混淆
  streamingText: string | null;
  activeAssistantIntro: string | null;
  activeProcessLogs: ProcessLogEntry[];
  attachments: AttachmentItem[];
  // 🔴 P0-2: 最后一次收到 SSE chunk 的时间戳，用于 polling 判断 SSE 是否仍活跃
  lastChunkReceivedAt: number | null;
  // 🔴 F-1: finalizeAssistantMessage 原子性标志（P2-2 新增，但未在接口声明）
  isFinalizing: boolean;
  // 🔵 流式会话结束标志，防止 abort 后回调重新初始化 builder
  streamEnded: boolean;
  // P1-6: 首页首消息延迟发送标志——AppNew 设置 input + 此标志，
  // AssistantPanel 挂载后检测并调用 handleSendMessage，消除 sendFirstMessage 双轨。
  pendingFirstMessage: boolean;
  // 技能显式调用：用户在输入框选择技能后发送时写入，_buildChatStreamOptions
  // 读取并消费（随 chatStream 请求体 skill_id 字段传给后端，短路 LLM 意图解析）
  pendingSkillId: string | null;
  // 技能显式调用：技能显示名（消息渲染为 @[skill:id|名称] 标签，与输入框技能标签视觉一致）
  pendingSkillName: string | null;
  // 🔴 P0-1: 已 finalize 的 assistant 消息 ID 集合（迁移自 streamState.finalizedMessageMap）
  // 非响应式：Map 值为 finalize 时间戳，10 分钟 TTL，size>50 触发 GC
  finalizedMessageIds: Map<string, number>;
  // 🔴 P0-1: 思考计时器（私有字段，迁移自 streamState.thinkingTimerMap）
  // 非响应式：由 startThinkingTimer / clearThinkingTimer / resetStreamingState 维护
  _thinkingTimers: Map<string, ReturnType<typeof setInterval>>;
  // ===== 中断决策方案（§3.9）：运行中插话状态 =====
  // 待处理插话数（收到 __INTERRUPT_RECEIVED__ +1，决策后 -1），输入框状态点
  pendingInterruptCount: number;
  setPendingInterruptCount: (count: number) => void;
  /**
   * 运行中插话发送（§3.9：send 在任务运行中自动改走 interrupt 端点，对调用方透明）。
   * 返回 true=已注入（本地乐观渲染用户气泡）；false=应回落常规发送。
   */
  sendInterruptMessage: (text: string) => Promise<boolean>;
  // §6 决策误判缓解：最近一次插话原文（纠错按钮据此重发）
  lastInterruptText: string;
  setLastInterruptText: (text: string) => void;
  // 交互改版（2026-10-14）：尾部轻提醒（未完成任务 + 继续按钮），非模态不打断。
  // 注意：不入 STREAMING_RESET（提醒帧在流收尾前到达，finalize 会立即清掉），
  // 仅在 SESSION_RESET（切会话）、发送新消息（useStreamHandler.handleSendMessage）、
  // 提醒卡点击/关闭时清除。
  // 15.3 多任务：tasks ≥2 条时前端列表化渲染（intent_name/display 主体字段
  // 保持最新任务语义，单任务时不含 tasks，行为与旧版一致）。
  taskReminder: {
    intentName: string;
    display: string;
    // 放弃状态回写（2026-10-15）：单任务卡主体状态（discarding=放弃中禁按钮、
    // discarded=已放弃终态、failed=回滚为可重试按钮）
    status?: 'discarding' | 'discarded' | 'failed';
    tasks?: Array<{
      intentName: string;
      display: string;
      status?: 'discarding' | 'discarded' | 'failed';
    }>;
  } | null;
  setTaskReminder: (reminder: {
    intentName: string;
    display: string;
    status?: 'discarding' | 'discarded' | 'failed';
    tasks?: Array<{
      intentName: string;
      display: string;
      status?: 'discarding' | 'discarded' | 'failed';
    }>;
  } | null) => void;
  /**
   * 放弃状态回写（2026-10-15）：discard_task 终态帧把提醒卡对应条目置状态。
   * 多任务按 intentName/display 匹配条目；单任务匹配主体；找不到条目
   * （手动打字触发、提醒卡已清）时静默忽略。
   */
  updateTaskReminderStatus: (intentName: string, status: 'discarding' | 'discarded' | 'failed') => void;
  /**
   * "不是这个意思"纠错（§6）：把最近一次插话以纠错标记重发，
   * 后端跳过 LLM 决策直接转 ask 弹窗。返回 true=已发送。
   */
  correctLastDecision: () => Promise<boolean>;

  // ===== 任务流改版：ActiveTask 单真源（Stream slice 的子模块：任务流） =====
  // 所有"是否为搜索/生成确认场景"的判断统一使用 activeTask.resultType === 'search' / 'confirm'
  // activeTask 为 null 表示当前不在流式回复中；resultType 决定场景类型
  // 在流式会话中，所有事件回调（文本 chunk / 搜索结果 / 生成确认等）都只更新此 activeTask
  // 完成时由 handleComplete 从 activeTask 生成最终消息 → 彻底消除"多条消息同时被追加"的竞态
  activeTask: ActiveTask | null;
  initActiveTask: (sessionId: string) => void;
  updateActiveTask: (patch: Partial<ActiveTask>) => void;
  updateActiveTaskMetadata: (patch: Record<string, any>) => void;
  setActiveTaskStage: (stage: TaskStage | null) => void;
  setActiveTaskIntro: (intro: string, skillName?: string, agentName?: string) => void;
  setActiveTaskAsk: (askData: TaskAskData | null) => void;
  // 时间线：把当前 content 存为 timeline 内容段并清空 content（task_ask 到来时调用）
  pushTimelineContent: () => void;
  // 时间线：追加已询问 Q&A 项（用户回答后调用）
  // 方案 A+ 修复：支持 attachments 字段，弹窗上传图片后"已询问"记录显示缩略图
  pushTimelineAsked: (item: { question: string; answer: string; attachments?: { url: string; name?: string }[] }) => void;
  // 时间线：移除最后一个 timeline 项（workflowReply 失败时回滚用）
  popTimelineLast: () => void;
  setActiveTaskResult: (resultType: ActiveTask['resultType'], resultData?: any) => void;

  // Stream actions
  setAbortController: (controller: AbortController | null) => void;
  setIsLoading: (loading: boolean) => void;
  setIsThinking: (thinking: boolean) => void;
  setThinkingSeconds: (seconds: number | ((prev: number) => number)) => void;
  // 🔴 P1-18: setter 接受 IntentInfo | null
  setCurrentIntent: (intent: IntentInfo | null) => void;
  setInput: (input: string | ((prev: string) => string)) => void;
  setStreamingText: (text: string | null | ((prev: string | null) => string | null)) => void;
  /**
   * F-M5: 统一 streamingText + activeTask.content 双写入口。
   *
   * 用于"需要同时更新两处"的场景（如 useStreamPolling 的进度提示、useChunkHandlers 的 __REPLACE__ 清空）。
   * - 同时更新 streamingText（文本场景的流式显示源 + "是否流式中"标志）
   * - 同时更新 activeTask.content（搜索/确认场景的最终值），仅当 activeTask 存在时
   *
   * 注意：仅"同时写两处"的地方使用此函数；只写一处的地方（如搜索场景只写 activeTask.content）
   * 仍使用 setStreamingText 或 updateActiveTask。
   */
  setStreamingTextEverywhere: (text: string | null) => void;
  setActiveAssistantIntro: (intro: string | null) => void;
  setActiveProcessLogs: (logs: ProcessLogEntry[] | ((prev: ProcessLogEntry[]) => ProcessLogEntry[])) => void;
  setAttachments: (attachments: AttachmentItem[] | ((prev: AttachmentItem[]) => AttachmentItem[])) => void;
  resetStreamingState: () => void;
  clearThinkingTimer: () => void;
  startThinkingTimer: () => void;
  // 🔴 P0-2: SSE chunk 到达时标记时间戳，供 polling 判断 SSE 是否仍活跃
  bumpChunkReceivedAt: () => void;
  // P1-6: 首页首消息延迟发送标志的 setter
  setPendingFirstMessage: (pending: boolean) => void;
  // 技能显式调用 setter（发送链路写入，_buildChatStreamOptions 消费后清空）
  setPendingSkillId: (id: string | null) => void;
  setPendingSkillName: (name: string | null) => void;
  // DUAL-5: isFinalizing / streamEnded 的 setter action
  // 替代绕过 action 的 useAgentStore.setState({ isFinalizing / streamEnded: ... }) 写法
  setIsFinalizing: (value: boolean) => void;
  setStreamEnded: (value: boolean) => void;
  // 🔴 P0-1: finalized 消息 ID 管理（迁移自 streamState.markMessageFinalized / isMessageFinalized）
  // 含 TTL 10 分钟清理 + size>50 GC，保留原 FINALIZED_TTL_MS=600_000 语义
  addFinalizedMessageId: (id: string | null | undefined) => void;
  hasFinalizedMessageId: (id: string | null | undefined) => boolean;
  clearFinalizedMessageIds: () => void;

  // ==========================================================================
  // 🟪 Slice 2.5: 方案C — per-session 并行活容器（后台流写入 + 切回换指针）
  // 分流判据与生命周期详见文件头部方案C 注释块
  // ==========================================================================
  /** 非响应式容器：Map mutate 不触发渲染，stage/restore 是仅有的响应式边界 */
  sessionLiveStates: Map<string, SessionLiveState>;
  /** 切走时暂存视图流式状态到容器（abortController 一并移交）；仅流式中有效 */
  stageSessionState: (sessionId: string) => void;
  /** 切回时换指针同步恢复视图（零网络零滞后）；命中返回 true 并删除容器条目 */
  restoreSessionState: (sessionId: string) => boolean;
  /** 后台流终结（完成/用户显式停止/错误放弃）时清理容器条目 */
  clearSessionLiveState: (sessionId: string) => void;
  /** 后台流读取容器条目（chunk handler 后台分支读 activeTask / 轮询读活跃时间戳用） */
  getSessionLiveState: (sessionId: string) => SessionLiveState | null;
  /** 后台流：updateActiveTask 的容器版（detectSearchIntent 切 resultType / markdown 缓冲追加 content） */
  updateLiveActiveTask: (sessionId: string, patch: Partial<ActiveTask>) => void;
  /** 后台流：setStreamingText 的容器版（绝对值写入，与 handleTextChunk 全量累积语义对齐） */
  writeLiveStreamingText: (sessionId: string, text: string | null) => void;
  /** 后台流：bumpChunkReceivedAt 的容器版（后台轮询 SSE 活跃判断改读容器时间戳，避免污染视图字段） */
  bumpLiveChunkReceivedAt: (sessionId: string) => void;
  /** 🔴 FE-1: 后台流：guard 路由器把后台期间到达的结构化事件缓冲进容器（切回重放） */
  bufferLiveStreamEvent: (sessionId: string, evt: BufferedStreamEvent) => void;
  /** 🔴 G2-2 修复: 后台流：heartbeat 容器版（仅刷新 savedAt 活跃度，长静默期连接存活的唯一信号） */
  touchSessionLiveState: (sessionId: string) => void;

  // ==========================================================================
  // 🟦 Slice 3: Config — 模型选择 / searchMode / skills（用户偏好型配置）
  // ==========================================================================
  currentModelId: string;
  availableModels: ChatModel[];
  searchMode: 'auto' | 'fast' | 'deep';
  skills: SkillInfo[];
  // Config actions
  setCurrentModel: (modelId: string) => void;
  setAvailableModels: (models: ChatModel[]) => void;
  /** LLM 模型列表共享拉取（单飞）：多组件挂载只发一次请求，失败落兜底列表 */
  ensureAvailableModels: () => Promise<void>;
  setSearchMode: (mode: 'auto' | 'fast' | 'deep') => void;
  setSkills: (skills: SkillInfo[]) => void;
}

initCrossTabSync((sessionId, messages, activeTask) => {
  const state = useAgentStore.getState();
  if (state.sessionId === sessionId) {
    // ASK-GAP-7: 同步 activeTask（含 askData），使其他 tab 可恢复询问态
    useAgentStore.setState({ messages, ...(activeTask !== undefined ? { activeTask } : {}) });
  }
});

// ============================================================================
// P2-21: Reset 层级文档
// ----------------------------------------------------------------------------
// 本文件定义两层 reset 常量，按"作用范围从小到大"组织：
//
// 1. STREAMING_RESET — 流式会话级重置（最小范围）
//    触发场景：
//      - 流式完成（finalizeAssistantMessage / useStreamPolling 回退路径）
//      - 流式错误（handleError）
//      - 历史会话恢复时清除上一会话残留的流式状态（loadSession）
//    清除内容：流式文本 / intro / processLogs / thinking 计时 / abortController /
//              lastChunkReceivedAt / isFinalizing / streamEnded
//    保留内容：messages / sessionId / currentAgentId / currentIntent / input /
//              attachments / activeTask（关键！保留以便 BottomConfirmSheet 询问态持续）
//
// 2. SESSION_RESET — 会话级重置（在 STREAMING_RESET 基础上扩展）
//    触发场景：
//      - resetSession()（用户主动重置整个会话）
//    扩展清除：messages / sessionId / currentAgentId / currentIntent / input /
//              attachments / activeTask
//    设计说明：SESSION_RESET = {...STREAMING_RESET, ...会话级字段}
//      通过 spread 继承 STREAMING_RESET 全部字段，避免两份字段列表手动同步。
//      覆盖的字段（messages/sessionId 等）显式列出，便于审查。
//
// 注意：不存在"CLOSE_SESSION_RESET"——关闭会话走 deleteSession API + resetSession()，
//       不需要单独的 reset 常量。
// ============================================================================

// STREAMING_RESET：流式状态重置（详见上方 P2-21 文档）
export const STREAMING_RESET = {
  streamingText: null as string | null,
  activeAssistantIntro: null as string | null,
  activeProcessLogs: [] as ProcessLogEntry[],
  isLoading: false,
  isThinking: false,
  thinkingSeconds: 0,
  abortController: null as AbortController | null,
  // 🔴 P0-2: 重置 lastChunkReceivedAt
  lastChunkReceivedAt: null as number | null,
  // 🔴 F-1: 重置 isFinalizing
  isFinalizing: false,
  // 🔵 重置流式会话结束标志
  streamEnded: false,
  // P1-6: 重置首消息延迟发送标志
  pendingFirstMessage: false,
  // 重置技能显式调用（切换会话时清除，避免误带到新会话）
  pendingSkillId: null as string | null,
  pendingSkillName: null as string | null,
  // 中断决策方案：重置待处理插话数（流式结束/会话切换）
  pendingInterruptCount: 0,
  // §6：纠错锚点文本随流式状态一并重置
  lastInterruptText: '',
  // P0-1 修复：不从 STREAMING_RESET 中清除 activeTask
  // 当 activeTask.askData 存在时（BottomConfirmSheet 显示中），清除会导致弹窗提前消失
  // 由 finalizeAssistantMessage 在合适时机清除
};

// SESSION_RESET：会话级重置（详见上方 P2-21 文档）
const SESSION_RESET = {
  ...STREAMING_RESET,
  messages: [] as Message[],
  sessionId: null as string | null,
  currentAgentId: null as string | null,
  // 🔴 P1-18: 默认 null（无意图），由 createOnIntentCallback 在收到 intent 事件时填充
  currentIntent: null,
  input: '',
  attachments: [] as AttachmentItem[],
  // 切换会话时清除 activeTask（不在询问状态中）
  activeTask: null as ActiveTask | null,
  // 交互改版（2026-10-14）：切会话清除尾部轻提醒（提醒归属原会话，防串显）
  // 注意：不入 STREAMING_RESET——提醒帧在流收尾前到达，finalize 内部的
  // STREAMING_RESET 会立即清掉，提醒卡将永远不显示。
  taskReminder: null as { intentName: string; display: string } | null,
};

// 🔴 P0-1: finalized 消息 ID 的 TTL（10 分钟，与 polling MAX_DURATION 对齐）
// 迁移自 streamState.FINALIZED_TTL_MS，供 addFinalizedMessageId / hasFinalizedMessageId 使用
const FINALIZED_TTL_MS = 600_000;

export const useAgentStore = create<AgentState>()((set, get) => {
  const makeSetter = createSetter<AgentState>(set);

  return {
    // ==========================================================================
    // 🟦 Slice 1: Session — 会话管理（messages / sessionId / projectId / agents / 历史）
    // ==========================================================================
    messages: [],
    sessionId: null,
    projectId: null,
    agents: [],
    currentAgentId: null,
    chatHistory: [],
    selectedHistoryId: null,
    showHistoryModal: false,

    // 🔴 FE-6: 显式实现（原 makeSetter('sessionId')）——同步维护 localStorage
    // 最后活跃会话标记。新聊天 setSessionId(null) 清除标记，防重进项目误开旧会话。
    setSessionId: (sessionId) => {
      set({ sessionId });
      _writeLastSessionMarker(sessionId, get().projectId);
    },
    setProjectId: makeSetter('projectId'),
    setMessages: (messagesOrUpdater, extra?) => {
      // P0-F3/P1-F3: 支持 extra 字段原子写入，替代绕过 action 的 setState 写法
      if (typeof messagesOrUpdater === 'function') {
        set((state) => ({ messages: messagesOrUpdater(state.messages), ...(extra || {}) }));
      } else {
        set({ messages: messagesOrUpdater, ...(extra || {}) });
      }
      // P2-D8: 标记本标签页本地写入 messages 的时间戳，让 crossTabSync 能过滤滞后广播
      const sid = get().sessionId;
      if (sid) markLocalUpdate(sid);
      get().saveToLocal();
    },
    setCurrentAgentId: makeSetter('currentAgentId'),

  loadAgents: async () => {
    try {
      const result = await AgentService.listAgents();
      // P0-F4: 保留已选 currentAgentId，避免多 tab 切换或重新挂载时丢失用户选择。
      // 仅当当前选择不存在于新列表时才清空（如 agent 被删除）。
      set((state) => ({
        agents: result.agents,
        currentAgentId:
          state.currentAgentId && result.agents.some((a) => a.id === state.currentAgentId)
            ? state.currentAgentId
            : null,
      }));
    } catch (err) {
      console.error('Failed to load agents:', err);
    }
  },

  ensureSession: async (projectId: string) => {
    const { sessionId } = get();
    if (sessionId) return sessionId;
    try {
      const result = await AgentService.createSession(projectId);
      // 🔴 G4-1 修复: await 期间用户可能已切换到历史会话（setSessionId 已
      // 指向 B），无条件覆盖会把视图指针跳回新建会话 A 而消息列表仍是 B，
      // 后续消息将以 A 身份错写。复检仍为空才写入；已切换则沿用用户选择。
      if (!get().sessionId) {
        set({ sessionId: result.sessionId, projectId });
        // 🔴 FE-6: 新建会话同样写标记（绕过 setSessionId action 的直接 set 需补写）
        _writeLastSessionMarker(result.sessionId, projectId);
      }
      return get().sessionId ?? result.sessionId;
    } catch (err) {
      console.error('Failed to create session:', err);
      const fallbackId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      if (!get().sessionId) {
        set({ sessionId: fallbackId, projectId });
        _writeLastSessionMarker(fallbackId, projectId);
        return fallbackId;
      }
      return get().sessionId!;
    }
  },

  // 历史会话恢复逻辑由 useChatHistory.loadSessionMessages 统一承担
  // （支持 isHistory/resetStream/updateChatHistory 三种 options）。

  resetSession: () => set(SESSION_RESET),

  // 🔴 方案B（阶段三 FE-5·保守）：本地缓存定位降级为纯离线兜底--
  // 正常恢复链路只读 DB（单一真源），bongai_agent_<sid> 仅在 DB 失败
  // （断网/后端异常）时由 useChatHistory.loadSessionMessages 的 catch 回退消费。
  // 触发频率说明：仅消息列表变更（发送/finalize/恢复加载）触发，流式 chunk
  // 走 setStreamingText 不经此路径，无高频写；notifyOtherTabs 广播职责保留。
  saveToLocal: async () => {
    const { sessionId, projectId, messages, currentModelId, activeTask } = get();
    if (sessionId && messages.length > 0) {
      try {
        await unifiedStorage.saveAgent(sessionId, { messages, currentModelId, projectId: projectId || '' });
        // ASK-GAP-7: 广播 activeTask 让其他 tab 恢复询问态
        notifyOtherTabs(sessionId, messages, activeTask);
      } catch (e) {
        console.warn('Failed to save to local cache:', e);
      }
    }
  },

  // ==========================================================================
  // 🟦 Slice 2: Stream — 流式状态 + 任务流（activeTask）+ 思考计时
  // ==========================================================================
  abortController: null,
  isLoading: false,
  isThinking: false,
  thinkingSeconds: 0,
  // 🔴 P1-18: 默认 null（无意图）
  currentIntent: null,
  input: '',
  streamingText: null,
  activeAssistantIntro: null,
  activeProcessLogs: [],
  attachments: [],
  // 🔴 P0-2: 初始值
  lastChunkReceivedAt: null,
  // 🔴 F-1: 初始值
  isFinalizing: false,
  // 🔵 流式会话结束标志初始值
  streamEnded: false,
  // P1-6: 首页首消息延迟发送标志初始值
  pendingFirstMessage: false,
  // 技能显式调用初始值
  pendingSkillId: null as string | null,
  pendingSkillName: null as string | null,
  // 🔴 P0-1: 迁移自 streamState.finalizedMessageMap / thinkingTimerMap（非响应式容器）
  finalizedMessageIds: new Map<string, number>(),
  _thinkingTimers: new Map<string, ReturnType<typeof setInterval>>(),
  // 任务流改版：activeTask 初始值
  activeTask: null,
  // 中断决策方案（§3.9）：待处理插话数初始值
  pendingInterruptCount: 0,
  // §6 决策误判缓解：最近一次插话原文（纠错按钮据此重发）
  lastInterruptText: '',
  // 交互改版（2026-10-14）：尾部轻提醒初始值
  taskReminder: null as { intentName: string; display: string } | null,

  // 中断决策方案：待处理插话计数 setter
  setPendingInterruptCount: makeSetter('pendingInterruptCount'),
  setLastInterruptText: makeSetter('lastInterruptText'),
  // 交互改版（2026-10-14）：尾部轻提醒 setter
  setTaskReminder: makeSetter('taskReminder'),
  // 放弃状态回写（2026-10-15）：按 intentName 匹配条目置状态（display 兜底）
  updateTaskReminderStatus: (intentName, status) => {
    const reminder = get().taskReminder;
    if (!reminder) return;
    // 多任务卡：按任务条目匹配
    if (reminder.tasks && reminder.tasks.length > 1) {
      let matched = false;
      const tasks = reminder.tasks.map((t) => {
        if (t.intentName === intentName || t.display === intentName) {
          matched = true;
          return { ...t, status };
        }
        return t;
      });
      if (matched) set({ taskReminder: { ...reminder, tasks } });
      return;
    }
    // 单任务卡：主体匹配（找不到条目静默忽略——提醒卡已清或非提醒卡触发）
    if (reminder.intentName === intentName || reminder.display === intentName) {
      set({ taskReminder: { ...reminder, status } });
    }
  },
  /**
   * "不是这个意思"纠错（§6）：把最近一次插话以纠错标记重发，
   * 后端跳过 LLM 决策直接转 ask 弹窗（标记消息不落库、不渲染气泡）。
   */
  correctLastDecision: async (): Promise<boolean> => {
    const state = get();
    if (!state.lastInterruptText || !state.isLoading) return false;
    try {
      const { AgentService, DECISION_CORRECTION_MARKER } = await import(
        '@/services/agent/AgentService'
      );
      const markerText = `${DECISION_CORRECTION_MARKER}${state.lastInterruptText}`;
      // 直接走 sendInterrupt（不经 sendInterruptMessage）：控制消息不渲染气泡、
      // 不覆盖 lastInterruptText（后端亦跳过用户消息落库）
      const result = await AgentService.sendInterrupt(state.sessionId || '', markerText);
      return result.status === 'injected';
    } catch (e) {
      console.error('[agentStore] correctLastDecision failed:', e);
      return false;
    }
  },
  /**
   * 运行中插话发送（§3.9）：send 在任务运行中自动改走 interrupt 端点。
   *
   * - injected → 本地乐观渲染用户气泡（后端已持久化），返回 true；
   * - no_active_task / disabled → 返回 false（调用方回落常规发送链路）；
   * - queue_full / duplicate → toast 提示（sonner），返回 true（不再回落，避免重复发送）。
   */
  sendInterruptMessage: async (text: string): Promise<boolean> => {
    const state = get();
    const sessionId = state.sessionId;
    if (!sessionId || !text.trim()) return false;
    try {
      const { AgentService } = await import('@/services/agent/AgentService');
      const result = await AgentService.sendInterrupt(sessionId, text.trim());
      if (result.status === 'injected') {
        // 乐观渲染用户气泡（后端同构落库；刷新后以 DB 为准）
        const optimistic: Message = {
          id: `local-interrupt-${Date.now()}`,
          role: 'user',
          type: 'text',
          text: text.trim(),
        };
        get().setMessages((prev) => [...prev, optimistic]);
        // §6 决策误判缓解：记录原文，供"不是这个意思"纠错重发
        get().setLastInterruptText(text.trim());
        return true;
      }
      if (result.status === 'queue_full' || result.status === 'duplicate') {
        const { toast } = await import('sonner');
        toast.message(result.message || '消息处理中，请稍候');
        return true;
      }
      // no_active_task / disabled → 回落常规发送
      return false;
    } catch (e) {
      console.error('[agentStore] sendInterruptMessage failed:', e);
      return false;
    }
  },

  setAbortController: makeSetter('abortController'),
  setIsLoading: makeSetter('isLoading'),
  setIsThinking: makeSetter('isThinking'),
  setThinkingSeconds: makeSetter('thinkingSeconds'),
  setCurrentIntent: makeSetter('currentIntent'),
  setInput: makeSetter('input'),
  // 🔴 P0-1 + F-8: 统一流式文本写入入口
  // 文本场景更新 streamingText;搜索/确认场景由 chunk handler 直接更新 activeTask.content
  // 渲染层先读 activeTask.content,回落至 streamingText
  setStreamingText: (text: string | null | ((prev: string | null) => string | null)) => {
    if (typeof text === 'function') {
      set((state) => {
        const resolved = text(state.streamingText);
        const normalized = resolved === '' ? null : resolved;
        return { streamingText: normalized };
      });
    } else {
      const normalized = text === '' ? null : text;
      set({ streamingText: normalized });
    }
  },
  // F-M5: 统一 streamingText + activeTask.content 双写入口
  // 同时更新 streamingText 和 activeTask.content（仅当 activeTask 存在时）。
  // 用于 useStreamPolling 进度提示、useChunkHandlers __REPLACE__ 清空等"必须双写"的场景，
  // 避免 streamingText 与 activeTask.content 不一致（finalizeAssistantMessage 读 activeTask.content 会丢失）。
  setStreamingTextEverywhere: (text: string | null) => {
    const normalized = text === '' ? null : text;
    set((state) => {
      if (!state.activeTask) {
        return { streamingText: normalized };
      }
      return {
        streamingText: normalized,
        activeTask: { ...state.activeTask, content: normalized },
      };
    });
  },
  // 🔴 P0-2: 记录收到 SSE chunk 的时间戳，供 polling 判断 SSE 是否仍活跃
  bumpChunkReceivedAt: () => set({ lastChunkReceivedAt: Date.now() }),
  // P1-6: 首页首消息延迟发送标志 setter
  setPendingFirstMessage: makeSetter('pendingFirstMessage'),
  // 技能显式调用 setter
  setPendingSkillId: makeSetter('pendingSkillId'),
  setPendingSkillName: makeSetter('pendingSkillName'),
  // DUAL-5: isFinalizing / streamEnded 的 setter action
  // 替代绕过 action 的 useAgentStore.setState({ isFinalizing / streamEnded: ... }) 写法
  setIsFinalizing: makeSetter('isFinalizing'),
  setStreamEnded: makeSetter('streamEnded'),
  // 🔴 P0-1: finalized 消息 ID 管理（迁移自 streamState.markMessageFinalized / isMessageFinalized）
  // 非响应式：直接 mutate Map 实例，不调用 set()，避免无谓重渲染
  // P3c: id 放宽为可空——action 自带空 ID 守卫（原 streamState 包装器接受 optional）
  addFinalizedMessageId: (id: string | null | undefined) => {
    if (!id) return;
    const map = get().finalizedMessageIds;
    map.set(id, Date.now());
    // 顺带清理过期条目（size > 50 触发 GC）
    if (map.size > 50) {
      const now = Date.now();
      for (const [fid, ts] of map) {
        if (now - ts > FINALIZED_TTL_MS) map.delete(fid);
      }
    }
  },
  hasFinalizedMessageId: (id: string | null | undefined): boolean => {
    if (!id) return false;
    const map = get().finalizedMessageIds;
    const ts = map.get(id);
    if (!ts) return false;
    if (Date.now() - ts > FINALIZED_TTL_MS) {
      map.delete(id);
      return false;
    }
    return true;
  },
  clearFinalizedMessageIds: () => {
    get().finalizedMessageIds.clear();
  },
  setActiveAssistantIntro: makeSetter('activeAssistantIntro'),
  setActiveProcessLogs: makeSetter('activeProcessLogs'),
  setAttachments: makeSetter('attachments'),
  setChatHistory: makeSetter('chatHistory'),
  setSelectedHistoryId: makeSetter('selectedHistoryId'),
  setShowHistoryModal: makeSetter('showHistoryModal'),

  // ===== 任务流改版：ActiveTask 方法 =====
  initActiveTask: (sessionId: string) => {
    const localId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    set({
      activeTask: {
        id: localId,
        sessionId,
        skillId: null,
        skillName: null,
        agentName: null,
        intro: null,
        currentStage: null,
        timeline: [],
        content: null,
        resultType: null,
        resultData: null,
        askData: null,
        status: 'active',
        generationOptions: null,
        metadata: {},
      },
    });
  },

  updateActiveTask: (patch: Partial<ActiveTask>) => {
    set((state) => {
      if (!state.activeTask) return state;
      return { activeTask: { ...state.activeTask, ...patch } };
    });
  },

  // 合并更新 activeTask.metadata（保留已有字段，仅覆盖 patch 中的键）
  updateActiveTaskMetadata: (patch: Record<string, any>) => {
    set((state) => {
      if (!state.activeTask) return state;
      return { activeTask: { ...state.activeTask, metadata: { ...state.activeTask.metadata, ...patch } } };
    });
  },

  setActiveTaskStage: (stage: TaskStage | null) => {
    set((state) => {
      if (!state.activeTask) return state;
      return { activeTask: { ...state.activeTask, currentStage: stage } };
    });
  },

  setActiveTaskIntro: (intro: string, skillName?: string, agentName?: string) => {
    set((state) => {
      if (!state.activeTask) return state;
      return {
        activeTask: {
          ...state.activeTask,
          intro,
          ...(skillName !== undefined ? { skillName } : {}),
          ...(agentName !== undefined ? { agentName } : {}),
        },
      };
    });
  },

  setActiveTaskAsk: (askData: TaskAskData | null) => {
    set((state) => {
      if (!state.activeTask) return state;
      return { activeTask: { ...state.activeTask, askData } };
    });
    // ASK-GAP-7: 通知其他 tab 同步 askData 变更
    const state = get();
    if (state.sessionId) {
      notifyOtherTabs(state.sessionId, state.messages, state.activeTask);
    }
  },

  // 时间线：把当前 content 存为 timeline 内容段并清空 content（task_ask 到来时调用）
  // 🔴 M12: 统一读点规则 — 优先读 activeTask.content（最终值），fallback 到 streamingText（流式临时值）
  pushTimelineContent: () => {
    set((state) => {
      if (!state.activeTask) return state;
      const currentContent = getStreamingDisplayContent(state);
      if (!currentContent || !currentContent.trim()) return state;
      const newItem: TimelineItem = {
        type: 'content',
        id: `tl-content-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        text: currentContent,
        timestamp: Date.now(),
      };
      return {
        activeTask: {
          ...state.activeTask,
          timeline: [...state.activeTask.timeline, newItem],
          content: null, // 清空 content，准备接收下一段输出
        },
        // 归档后同步清空流式缓冲：getStreamingDisplayContent 会兜底读
        // streamingText，若只清 content，下一次归档会把"已归档全文+新增"
        // 再次入列，造成确认语/指引重复 3 份与段间拼接
        streamingText: null,
      };
    });
  },

  // 时间线：追加已询问 Q&A 项（用户回答后调用）
  // 方案 A+ 修复：支持 attachments 字段，弹窗上传图片后"已询问"记录显示缩略图
  pushTimelineAsked: (item: { question: string; answer: string; attachments?: { url: string; name?: string }[] }) => {
    set((state) => {
      if (!state.activeTask) return state;
      const newItem: TimelineItem = {
        type: 'asked',
        id: `tl-asked-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        question: item.question,
        answer: item.answer,
        timestamp: Date.now(),
        attachments: item.attachments,
      } as TimelineItem;
      return {
        activeTask: {
          ...state.activeTask,
          timeline: [...state.activeTask.timeline, newItem],
        },
      };
    });
  },

  // 时间线：移除最后一个 timeline 项（workflowReply 失败时回滚用）
  popTimelineLast: () => {
    set((state) => {
      if (!state.activeTask) return state;
      if (state.activeTask.timeline.length === 0) return state;
      return {
        activeTask: {
          ...state.activeTask,
          timeline: state.activeTask.timeline.slice(0, -1),
        },
      };
    });
  },

  setActiveTaskResult: (resultType: ActiveTask['resultType'], resultData?: any) => {
    set((state) => {
      if (!state.activeTask) return state;
      return {
        activeTask: {
          ...state.activeTask,
          resultType,
          resultData: resultData !== undefined ? resultData : state.activeTask.resultData,
        },
      };
    });
  },

  resetStreamingState: () => {
    const currentSessionId = get().sessionId;
    if (currentSessionId) {
      const timers = get()._thinkingTimers;
      if (timers.has(currentSessionId)) {
        clearInterval(timers.get(currentSessionId)!);
        timers.delete(currentSessionId);
      }
      // P1-H7: 不再删除 thinkingSecondsMap——秒数走 store.thinkingSeconds，
      // 由下方 STREAMING_RESET（含 thinkingSeconds: 0）统一重置。
    }
    set({ ...STREAMING_RESET });
  },

  clearThinkingTimer: () => {
    const currentSessionId = get().sessionId;
    const timers = get()._thinkingTimers;
    if (currentSessionId && timers.has(currentSessionId)) {
      clearInterval(timers.get(currentSessionId)!);
      timers.delete(currentSessionId);
    }
  },

  startThinkingTimer: () => {
    const currentSessionId = get().sessionId;
    if (!currentSessionId) return;

    const timers = get()._thinkingTimers;
    if (timers.has(currentSessionId)) {
      clearInterval(timers.get(currentSessionId)!);
    }
    timers.set(currentSessionId, setInterval(() => {
      const sid = get().sessionId;
      // P1-H7: 秒数直接通过 store.thinkingSeconds 递增（_thinkingTimers 仅存 timer）。
      // 仅当当前 session 仍是启动时的 session 时递增，避免会话切换后旧计时器污染新会话。
      if (sid && timers.has(sid)) {
        set((state) => ({ thinkingSeconds: state.thinkingSeconds + 1 }));
      }
    }, 1000));
  },

  // ==========================================================================
  // 🟪 Slice 2.5: 方案C — per-session 并行活容器（后台流写入 + 切回换指针）
  // ==========================================================================
  sessionLiveStates: new Map<string, SessionLiveState>(),

  // 🔴 方案C: 切走暂存——视图流式字段快照进容器，SSE controller 移交容器持有。
  // 调用点：useChatHistory 切换段（原 abort 位置之前）。
  // 仅流式中有效（无 controller / activeTask / streamingText 的空闲切换直接跳过）。
  // activeTask 必须归属目标会话：SESSION_RESET 不清 activeTask，切换后残留的
  // 其他会话 activeTask 不得快照进容器（否则孤儿条目会在切回时恢复出错误任务卡片）。
  // 不修改任何视图状态——视图清理由既有切换流程（resetSession / STREAMING_RESET）承担。
  stageSessionState: (sessionId) => {
    const s = get();
    const taskBelongsToSession = !s.activeTask || s.activeTask.sessionId === sessionId;
    const isStreaming =
      taskBelongsToSession &&
      (!!s.abortController || s.isLoading || s.isThinking || !!s.activeTask || s.streamingText != null);
    if (!isStreaming) return;
    s.sessionLiveStates.set(sessionId, {
      savedAt: Date.now(),
      abortController: s.abortController,
      messages: s.messages,
      streamingText: s.streamingText,
      activeAssistantIntro: s.activeAssistantIntro,
      activeProcessLogs: s.activeProcessLogs,
      activeTask: s.activeTask,
      isLoading: s.isLoading,
      isThinking: s.isThinking,
      thinkingSeconds: s.thinkingSeconds,
      lastChunkReceivedAt: s.lastChunkReceivedAt,
      currentIntent: s.currentIntent,
      // FE-1: 切走瞬间重置事件缓冲（覆盖旧条目即清空）
      pendingEvents: [],
    });
    console.info(`[agentStore] 方案C stage: 会话 ${sessionId} 流式状态移交容器（SSE 保活）`);
  },

  // 🔴 方案C: 切回换指针——容器命中则同步恢复视图（零网络零滞后），并删除条目
  // （流回前台后容器职责结束，写入经分流判据自动回到视图；二次切走由 stage 重新快照）。
  // abortController 随之归还视图，用户在新视图点「停止」可正确中止该流。
  restoreSessionState: (sessionId) => {
    const s = get();
    const live = s.sessionLiveStates.get(sessionId);
    if (!live) return false;
    set({
      messages: live.messages,
      streamingText: live.streamingText,
      activeAssistantIntro: live.activeAssistantIntro,
      activeProcessLogs: live.activeProcessLogs,
      activeTask: live.activeTask,
      isLoading: live.isLoading,
      isThinking: live.isThinking,
      thinkingSeconds: live.thinkingSeconds,
      abortController: live.abortController,
      lastChunkReceivedAt: live.lastChunkReceivedAt,
      currentIntent: live.currentIntent,
    });
    s.sessionLiveStates.delete(sessionId);
    // 🔴 FE-1: 重放切走期间缓冲的结构化事件（事件黑洞根因修复）--
    // 视图字段已恢复、sessionId 已等于归属会话（setSessionId 先于加载），
    // replay 内部的归属守卫自然放行，事件按到达顺序作用于恢复后的视图：
    // search_result 进 activeTask.resultData、task_ask 重建询问弹窗、
    // task_stage 补齐时间线。JS 单线程保证重放期间不会被新事件插队。
    const pendingEvents = live.pendingEvents ?? [];
    for (const evt of pendingEvents) {
      try {
        evt.replay();
      } catch (err) {
        console.warn(`[agentStore] FE-1 重放事件 ${evt.type} 失败:`, err);
      }
    }
    // 🔴 方案C: 恢复思考态时重启计时器--切走时 clearThinkingTimer 已停掉旧
    // interval（计时器是视图私有，不随容器快照），后台流期间秒数冻结在快照值，
    // 切回续走。startThinkingTimer 按视图 sessionId 建键（此时已是目标会话），
    // 后续首 chunk 到达由 handleTextChunk 前台分支正常停表。
    if (live.isThinking) {
      get().startThinkingTimer();
    }
    return true;
  },

  // 🔴 方案C: 后台流终结（完成/用户显式停止/错误放弃）时清理容器条目。
  // 完成消息已由后端落库（_finalize_and_persist），切回走 DB 恢复，容器无保留价值。
  clearSessionLiveState: (sessionId) => {
    get().sessionLiveStates.delete(sessionId);
  },

  // 🔴 方案C: 后台流读取容器条目（返回引用，只读语义——写入必须走 update/write/bump API）
  // 使用方：useChunkHandlers 后台分支读 activeTask / useStreamPolling 读活跃时间戳
  getSessionLiveState: (sessionId) => {
    return get().sessionLiveStates.get(sessionId) ?? null;
  },

  // 🔴 方案C: updateActiveTask 的容器版——条目缺失时丢弃写入（时序异常由 DB 轮询兜底）
  updateLiveActiveTask: (sessionId, patch) => {
    const live = get().sessionLiveStates.get(sessionId);
    if (!live || !live.activeTask) return;
    live.activeTask = { ...live.activeTask, ...patch };
    live.savedAt = Date.now();
  },

  // 🔴 方案C: setStreamingText 的容器版（绝对值写入，与 handleTextChunk 全量累积语义对齐；
  // 空串归一化为 null，与视图版行为一致）
  writeLiveStreamingText: (sessionId, text) => {
    const live = get().sessionLiveStates.get(sessionId);
    if (!live) return;
    live.streamingText = text === '' ? null : text;
    live.savedAt = Date.now();
  },

  // 🔴 方案C: bumpChunkReceivedAt 的容器版——后台流的 chunk 时间戳写容器，
  // 不污染视图 lastChunkReceivedAt（否则新会话的前台轮询 SSE 活跃判断被旧流干扰）
  bumpLiveChunkReceivedAt: (sessionId) => {
    const live = get().sessionLiveStates.get(sessionId);
    if (!live) return;
    live.lastChunkReceivedAt = Date.now();
    live.savedAt = Date.now();
  },

  // 🔴 G2-2 修复: heartbeat 容器版——只刷 savedAt（FE-5 看门狗与 FE-2 死
  // 容器判据共用该时间戳）。后端长静默期每 3s 一跳，若不刷新则连接活着
  // 也被误判死亡（看门狗误杀前台流 / 切回时容器误降级走 DB 滞后态）。
  // 前台流式时容器条目不存在（stage 仅切走时创建），no-op 安全。
  touchSessionLiveState: (sessionId) => {
    const live = get().sessionLiveStates.get(sessionId);
    if (!live) return;
    live.savedAt = Date.now();
  },

  // 🔴 FE-1: guard 路由器的容器版写入--后台期间到达的结构化事件缓冲进容器，
  // 切回 restoreSessionState 时依序重放。state 类事件（search_result/task_ask/
  // intent 等，回调语义为"设置最新状态"）同 type 去重仅保留最新；
  // append 类事件（task_stage/thinking 等，回调语义为"追加"）依序保留，
  // 超上限（500）时优先丢弃最旧追加类。条目缺失（流已终结容器被清）时丢弃，
  // 由 DB 轮询兜底。
  bufferLiveStreamEvent: (sessionId, evt) => {
    const live = get().sessionLiveStates.get(sessionId);
    if (!live) return;
    if (evt.kind === 'state') {
      const idx = live.pendingEvents.findIndex(e => e.type === evt.type);
      if (idx >= 0) live.pendingEvents.splice(idx, 1);
    }
    live.pendingEvents.push(evt);
    if (live.pendingEvents.length > LIVE_EVENT_BUFFER_MAX) {
      const dropIdx = live.pendingEvents.findIndex(e => e.kind === 'append');
      if (dropIdx >= 0) live.pendingEvents.splice(dropIdx, 1);
      else live.pendingEvents.shift();
    }
    live.savedAt = Date.now();
  },

  // ==========================================================================
  // 🟦 Slice 3: Config — 模型选择 / searchMode / skills（用户偏好型配置）
  // ==========================================================================
  currentModelId: DEFAULT_CHAT_MODEL_ID,
  availableModels: [],
  searchMode: 'auto',
  skills: [],

  setCurrentModel: (modelId) => { set({ currentModelId: modelId }); get().saveToLocal(); },
  setAvailableModels: makeSetter('availableModels'),
  // 单飞：并发调用共享同一个 Promise；请求结束清空句柄（失败后允许下次重试）
  ensureAvailableModels: async () => {
    if (_modelsFetchPromise) return _modelsFetchPromise;
    _modelsFetchPromise = (async () => {
      try {
        const data = await modelApi.getLlmModels();
        const chatModels: ChatModel[] = (data || []).map((m: { id: string; name: string; backend: string; supportedModes?: string[] }) => ({
          id: m.id,
          name: m.name,
          backend: m.backend,
          supportedModes: m.supportedModes || ['chat'],
        }));
        set({
          availableModels: chatModels,
          currentModelId: sanitizeCurrentModelId(get().currentModelId, chatModels),
        });
      } catch {
        set({
          availableModels: FALLBACK_LLM_MODELS,
          currentModelId: sanitizeCurrentModelId(get().currentModelId, FALLBACK_LLM_MODELS),
        });
      }
    })();
    try {
      await _modelsFetchPromise;
    } finally {
      _modelsFetchPromise = null;
    }
  },
  setSearchMode: makeSetter('searchMode'),
  setSkills: makeSetter('skills'),
  };
});

/**
 * 统一读取流式显示内容(M15 单一读点)。
 *
 * 🔴 P3c（清退）: 原 streamState.ts 兼容层已删除，本函数迁入 store 模块
 * （消除 agentStore ↔ streamState 循环导入）。
 *
 * 优先级: activeTask?.content(最终值) > streamingText(流式临时值)。
 * - activeTask.content: 搜索/确认场景的最终值,由 chunk handler 的 markdown 缓冲逻辑维护
 * - streamingText: 文本场景的流式显示源 + "是否正在流式中"标志(非 null 表示流式中)
 *
 * 注意: P3-M15 评估结论——读点已统一,但写入点保留双轨
 *   (文本场景走 setStreamingText,搜索/确认场景走 updateActiveTask),
 *   原因: 两者更新路径不同且 streamingText 兼任"是否流式中"标志,合并会引入高渲染开销与语义混淆。
 *   写入点清单: useChunkHandlers / useStreamCallbacks / useStreamHandler / useChatHistory /
 *              useSendMessageHandlers / useStreamPolling / useTaskStageCallback / chatHistoryHelpers /
 *              useStreamLifecycle / AppNew.tsx
 */
export function getStreamingDisplayContent(
  store: { activeTask: { content: string | null } | null; streamingText: string | null },
): string | null {
  return store.activeTask?.content || store.streamingText;
}
